import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Building2, Plus, Edit2, X, CheckCircle, XCircle, Ambulance, UserCheck, FileSpreadsheet } from 'lucide-react'

import { apiClient } from '../services/api'
import LoadingSpinner from '../components/LoadingSpinner'

interface AutoProvisioningDefaults {
  can_review_invoices?: boolean
  can_view_reports?: boolean
  can_order_ambulance?: boolean
  can_review_ambulance?: boolean
}

interface FinaSettings {
  enabled?: boolean
  profile?: string  // "kshva" | "pshva"
  invoice_number_start?: number
  invoice_number_end?: number
}

interface OrganizationSettings {
  features?: {
    ambulance_orders_enabled?: boolean
    auto_provisioning_enabled?: boolean
  }
  auto_provisioning?: {
    default_permissions?: AutoProvisioningDefaults
  }
  fina?: FinaSettings
}

interface Organization {
  id: string
  name: string
  entra_tenant_id: string
  is_active: boolean
  is_system_admin: boolean
  allowed_domains: string[]
  allowed_entra_tenants: string[]
  settings: OrganizationSettings
  consent_verified: boolean
  consent_verified_at: string | null
  access_code_prefix: string | null
  ahola_sub_org_uuid: string | null
  contract_number: string | null
  created_at: string
}

interface OrganizationCreate {
  name: string
  entra_tenant_id: string
  allowed_domains: string[]
  allowed_entra_tenants: string[]
}

interface OrganizationUpdate {
  name?: string
  allowed_domains?: string[]
  allowed_entra_tenants?: string[]
  settings?: OrganizationSettings
  access_code_prefix?: string | null
  ahola_sub_org_uuid?: string | null
  contract_number?: string | null
}

export default function OrganizationsPage() {
  const { t } = useTranslation('admin')
  const { t: tc } = useTranslation('common')
  const queryClient = useQueryClient()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingOrg, setEditingOrg] = useState<Organization | null>(null)
  const [domainsInput, setDomainsInput] = useState('')
  const [tenantsInput, setTenantsInput] = useState('')
  const [ambulanceEnabled, setAmbulanceEnabled] = useState(false)
  const [autoProvisioningEnabled, setAutoProvisioningEnabled] = useState(false)
  const [autoProvDefaults, setAutoProvDefaults] = useState<AutoProvisioningDefaults>({
    can_review_invoices: false,
    can_view_reports: false,
    can_order_ambulance: false,
    can_review_ambulance: false,
  })
  const [prefixInput, setPrefixInput] = useState('')
  const [aholaUuidInput, setAholaUuidInput] = useState('')
  const [contractInput, setContractInput] = useState('')
  const [finaEnabled, setFinaEnabled] = useState(false)
  const [finaSettings, setFinaSettings] = useState<FinaSettings>({})
  const [newOrg, setNewOrg] = useState<OrganizationCreate>({
    name: '',
    entra_tenant_id: '',
    allowed_domains: [],
    allowed_entra_tenants: [],
  })

  const { data, isLoading, error } = useQuery<{ data: Organization[] }>({
    queryKey: ['organizations'],
    queryFn: () => apiClient.get('/organizations'),
  })

  const createOrgMutation = useMutation({
    mutationFn: (orgData: OrganizationCreate) => apiClient.post('/organizations', orgData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizations'] })
      setIsModalOpen(false)
      resetForm()
    },
  })

  const updateOrgMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: OrganizationUpdate }) =>
      apiClient.patch(`/organizations/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizations'] })
      setEditingOrg(null)
      resetForm()
    },
  })

  const resetForm = () => {
    setNewOrg({
      name: '',
      entra_tenant_id: '',
      allowed_domains: [],
      allowed_entra_tenants: [],
    })
    setDomainsInput('')
    setTenantsInput('')
    setAmbulanceEnabled(false)
    setAutoProvisioningEnabled(false)
    setAutoProvDefaults({
      can_review_invoices: false,
      can_view_reports: false,
      can_order_ambulance: false,
      can_review_ambulance: false,
    })
    setPrefixInput('')
    setAholaUuidInput('')
    setContractInput('')
    setFinaEnabled(false)
    setFinaSettings({})
  }

  const openEditModal = (org: Organization) => {
    setEditingOrg(org)
    setNewOrg({
      name: org.name,
      entra_tenant_id: org.entra_tenant_id,
      allowed_domains: org.allowed_domains || [],
      allowed_entra_tenants: org.allowed_entra_tenants || [],
    })
    setDomainsInput((org.allowed_domains || []).join(', '))
    setTenantsInput((org.allowed_entra_tenants || []).join(', '))
    setAmbulanceEnabled(org.settings?.features?.ambulance_orders_enabled ?? false)
    setAutoProvisioningEnabled(org.settings?.features?.auto_provisioning_enabled ?? false)
    setAutoProvDefaults(org.settings?.auto_provisioning?.default_permissions ?? {
      can_review_invoices: false,
      can_view_reports: false,
      can_order_ambulance: false,
      can_review_ambulance: false,
    })
    setPrefixInput(org.access_code_prefix || '')
    setAholaUuidInput(org.ahola_sub_org_uuid || '')
    setContractInput(org.contract_number || '')
    setFinaEnabled(org.settings?.fina?.enabled ?? false)
    setFinaSettings(org.settings?.fina ?? {})
  }

  const handleEditSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingOrg) return
    const domains = domainsInput.split(',').map(d => d.trim().toLowerCase()).filter(d => d)
    const tenants = tenantsInput.split(',').map(t => t.trim()).filter(t => t)
    updateOrgMutation.mutate({
      id: editingOrg.id,
      data: {
        name: newOrg.name,
        allowed_domains: domains,
        allowed_entra_tenants: tenants,
        access_code_prefix: prefixInput.trim() || null,
        ahola_sub_org_uuid: aholaUuidInput.trim() || null,
        contract_number: contractInput.trim() || null,
        settings: {
          ...editingOrg.settings,
          features: {
            ...editingOrg.settings?.features,
            ambulance_orders_enabled: ambulanceEnabled,
            auto_provisioning_enabled: autoProvisioningEnabled,
          },
          auto_provisioning: {
            ...editingOrg.settings?.auto_provisioning,
            default_permissions: autoProvDefaults,
          },
          fina: {
            ...finaSettings,
            enabled: finaEnabled,
          },
        },
      },
    })
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const domains = domainsInput.split(',').map(d => d.trim().toLowerCase()).filter(d => d)
    const tenants = tenantsInput.split(',').map(t => t.trim()).filter(t => t)
    createOrgMutation.mutate({
      ...newOrg,
      allowed_domains: domains,
      allowed_entra_tenants: tenants,
    })
  }

  if (isLoading) {
    return <LoadingSpinner />
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">{tc('errors.generic')}</p>
      </div>
    )
  }

  const organizations = data?.data || []

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('organizations.title') || 'Organisaatiot'}</h1>
          <p className="mt-1 text-gray-600">{t('organizations.subtitle') || 'Hallinnoi asiakasorganisaatioita'}</p>
        </div>
        <button 
          onClick={() => setIsModalOpen(true)}
          className="inline-flex items-center px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors"
        >
          <Plus className="w-5 h-5 mr-2" />
          {t('organizations.addOrg') || 'Lisää organisaatio'}
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('organizations.name') || 'Nimi'}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('organizations.prefix') || 'Etuliite'}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('organizations.domains') || 'Domainit'}
                </th>
                <th className="px-2 py-3 w-8"></th>
                <th className="px-2 py-3 w-8"></th>
                <th className="px-2 py-3 w-8"></th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Consent
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('organizations.status') || 'Tila'}
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('organizations.actions') || 'Toiminnot'}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {organizations.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-6 py-12 text-center text-gray-500">
                    {t('organizations.noOrgs') || 'Ei organisaatioita'}
                  </td>
                </tr>
              ) : (
                organizations.map((org) => (
                  <tr key={org.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Building2 className="w-5 h-5 text-gray-400 mr-3" />
                        <div>
                          <div className="text-sm font-medium text-gray-900">{org.name}</div>
                          {org.is_system_admin && (
                            <span className="text-xs text-purple-600 font-medium">System Admin</span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {org.access_code_prefix ? (
                        <span className="px-2 py-0.5 text-xs font-mono font-medium bg-purple-100 text-purple-700 rounded">
                          {org.access_code_prefix}
                        </span>
                      ) : (
                        <span className="text-gray-400 text-sm">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1">
                        {(org.allowed_domains || []).map((domain, i) => (
                          <span key={i} className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded">
                            {domain}
                          </span>
                        ))}
                        {(!org.allowed_domains || org.allowed_domains.length === 0) && (
                          <span className="text-gray-400 text-sm">-</span>
                        )}
                      </div>
                    </td>
                    <td className="px-2 py-4 whitespace-nowrap text-center">
                      {org.settings?.features?.ambulance_orders_enabled && (
                        <span title={t('organizations.ambulanceOrdersEnabled')}>
                          <Ambulance className="w-4 h-4 text-blue-600 inline-block" />
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-4 whitespace-nowrap text-center">
                      {org.settings?.features?.auto_provisioning_enabled && (
                        <span title={t('organizations.autoProvisioningEnabled')}>
                          <UserCheck className="w-4 h-4 text-green-600 inline-block" />
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-4 whitespace-nowrap text-center">
                      {org.settings?.fina?.enabled && (
                        <span title="FINA">
                          <FileSpreadsheet className="w-4 h-4 text-purple-600 inline-block" />
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {org.consent_verified ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                          <CheckCircle className="w-3 h-3" />
                          Verified
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-amber-100 text-amber-800">
                          <XCircle className="w-3 h-3" />
                          Pending
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${
                        org.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {org.is_active ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                        {org.is_active ? tc('common.active') : tc('common.inactive')}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                      <button 
                        onClick={() => openEditModal(org)}
                        className="text-gray-400 hover:text-primary-600"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit Organization Modal */}
      {editingOrg && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b shrink-0">
              <h2 className="text-lg font-semibold">{t('organizations.editOrg')}</h2>
              <button onClick={() => { setEditingOrg(null); resetForm(); }} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleEditSubmit} className="p-6 space-y-4 overflow-y-auto">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('organizations.name')} *
                </label>
                <input
                  type="text"
                  required
                  value={newOrg.name}
                  onChange={(e) => setNewOrg({ ...newOrg, name: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Azure Entra Tenant ID
                </label>
                <input
                  type="text"
                  disabled
                  value={newOrg.entra_tenant_id}
                  className="w-full px-3 py-2 border rounded-lg bg-gray-100 font-mono text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('organizations.domains')}
                </label>
                <input
                  type="text"
                  value={domainsInput}
                  onChange={(e) => setDomainsInput(e.target.value)}
                  placeholder="esim. yritys.fi, yritys.com (pilkulla erotettuna)"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('organizations.allowedTenants')}
                </label>
                <input
                  type="text"
                  value={tenantsInput}
                  onChange={(e) => setTenantsInput(e.target.value)}
                  placeholder="Pilkulla erotettuna (valinnainen)"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 font-mono text-sm"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('organizations.prefix') || 'Etuliite'}
                  </label>
                  <input
                    type="text"
                    maxLength={2}
                    value={prefixInput}
                    onChange={(e) => setPrefixInput(e.target.value.toUpperCase())}
                    placeholder="KS"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 font-mono text-sm uppercase"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Ahola Sub Org UUID
                  </label>
                  <input
                    type="text"
                    value={aholaUuidInput}
                    onChange={(e) => setAholaUuidInput(e.target.value)}
                    placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 font-mono text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('organizations.contractNumber') || 'Sopimusnumero'}
                </label>
                <input
                  type="text"
                  value={contractInput}
                  onChange={(e) => setContractInput(e.target.value)}
                  placeholder="esim. 12345"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              {/* Features section */}
              <div className="pt-4 border-t">
                <h3 className="text-sm font-medium text-gray-700 mb-3">
                  {t('organizations.features') || 'Ominaisuudet'}
                </h3>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={ambulanceEnabled}
                    onChange={(e) => setAmbulanceEnabled(e.target.checked)}
                    className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  />
                  <Ambulance className="w-5 h-5 text-red-500" />
                  <span className="text-sm text-gray-700">
                    {t('organizations.ambulanceOrdersEnabled') || 'Ambulanssitilaukset käytössä'}
                  </span>
                </label>

                <label className="flex items-center gap-3 cursor-pointer mt-3">
                  <input
                    type="checkbox"
                    checked={autoProvisioningEnabled}
                    onChange={(e) => setAutoProvisioningEnabled(e.target.checked)}
                    className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  />
                  <span className="text-sm text-gray-700">
                    {t('organizations.autoProvisioningEnabled') || 'Automaattinen käyttäjäluonti'}
                  </span>
                </label>

                {autoProvisioningEnabled && (
                  <div className="ml-7 mt-2 space-y-2">
                    <p className="text-xs text-gray-500">
                      {t('organizations.autoProvisioningDefaults') || 'Oletuksena automaattisesti luoduilla käyttäjillä:'}
                    </p>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={autoProvDefaults.can_review_invoices ?? false}
                        onChange={(e) => setAutoProvDefaults(prev => ({ ...prev, can_review_invoices: e.target.checked }))}
                        className="w-3.5 h-3.5 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                      />
                      <span className="text-sm text-gray-600">
                        {t('organizations.permCanReviewInvoices') || 'Laskujen tarkastus'}
                      </span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={autoProvDefaults.can_view_reports ?? false}
                        onChange={(e) => setAutoProvDefaults(prev => ({ ...prev, can_view_reports: e.target.checked }))}
                        className="w-3.5 h-3.5 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                      />
                      <span className="text-sm text-gray-600">
                        {t('organizations.permCanViewReports') || 'Raporttien katselu'}
                      </span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={autoProvDefaults.can_order_ambulance ?? false}
                        onChange={(e) => setAutoProvDefaults(prev => ({ ...prev, can_order_ambulance: e.target.checked }))}
                        className="w-3.5 h-3.5 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                      />
                      <span className="text-sm text-gray-600">
                        {t('organizations.permCanOrderAmbulance') || 'Ambulanssitilaukset'}
                      </span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={autoProvDefaults.can_review_ambulance ?? false}
                        onChange={(e) => setAutoProvDefaults(prev => ({ ...prev, can_review_ambulance: e.target.checked }))}
                        className="w-3.5 h-3.5 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                      />
                      <span className="text-sm text-gray-600">
                        {t('organizations.permCanReviewAmbulance') || 'Ambulanssitarkastus'}
                      </span>
                    </label>
                  </div>
                )}
              </div>

              {/* FINA Settings section */}
              <div className="pt-4 border-t">
                <h3 className="text-sm font-medium text-gray-700 mb-3">FINA-asetukset</h3>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={finaEnabled}
                    onChange={(e) => setFinaEnabled(e.target.checked)}
                    className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  />
                  <span className="text-sm text-gray-700">FINA käytössä</span>
                </label>

                {finaEnabled && (
                  <div className="ml-7 mt-3 space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">{t('organizations.finaProfile') || 'Hyvinvointialue'}</label>
                      <select
                        value={finaSettings.profile || ''}
                        onChange={(e) => setFinaSettings((s) => ({ ...s, profile: e.target.value }))}
                        className="w-full px-2 py-1.5 border rounded text-sm focus:ring-2 focus:ring-primary-500"
                      >
                        <option value="">{t('organizations.selectProfile') || '— Valitse —'}</option>
                        <option value="kshva">KSHVA — Keski-Suomen hyvinvointialue</option>
                        <option value="pshva">PSHVA — Pohjois-Savon hyvinvointialue</option>
                      </select>
                    </div>
                    {finaSettings.profile && (
                      <>
                        <div className="rounded bg-gray-50 px-3 py-2 text-xs text-gray-500">
                          {finaSettings.profile === 'kshva' ? (
                            <span>eSote / eSote75 / 00M / Laskunro 5800000001–5899999999</span>
                          ) : (
                            <span>TAKSIHELSINKI / HVA31 / M01 / Laskunro 311000001–311999999</span>
                          )}
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">{t('organizations.invoiceNumberStart') || 'Laskunumero alku'}</label>
                            <input
                              type="number"
                              value={finaSettings.invoice_number_start || ''}
                              onChange={(e) => setFinaSettings((s) => ({ ...s, invoice_number_start: e.target.value ? Number(e.target.value) : undefined }))}
                              placeholder={finaSettings.profile === 'kshva' ? '5800000001' : '311000001'}
                              className="w-full px-2 py-1.5 border rounded text-sm font-mono focus:ring-2 focus:ring-primary-500"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">{t('organizations.invoiceNumberEnd') || 'Laskunumero loppu'}</label>
                            <input
                              type="number"
                              value={finaSettings.invoice_number_end || ''}
                              onChange={(e) => setFinaSettings((s) => ({ ...s, invoice_number_end: e.target.value ? Number(e.target.value) : undefined }))}
                              placeholder={finaSettings.profile === 'kshva' ? '5899999999' : '311999999'}
                              className="w-full px-2 py-1.5 border rounded text-sm font-mono focus:ring-2 focus:ring-primary-500"
                            />
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => { setEditingOrg(null); resetForm(); }}
                  className="px-4 py-2 text-gray-700 border rounded-lg hover:bg-gray-50"
                >
                  {tc('common.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={updateOrgMutation.isPending}
                  className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 disabled:opacity-50"
                >
                  {updateOrgMutation.isPending ? tc('common.saving') : tc('common.save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Organization Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="text-lg font-semibold">{t('organizations.addOrg') || 'Lisää organisaatio'}</h2>
              <button onClick={() => { setIsModalOpen(false); resetForm(); }} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('organizations.name') || 'Nimi'} *
                </label>
                <input
                  type="text"
                  required
                  value={newOrg.name}
                  onChange={(e) => setNewOrg({ ...newOrg, name: e.target.value })}
                  placeholder="Esim. Yritys Oy"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Azure Entra Tenant ID *
                </label>
                <input
                  type="text"
                  required
                  value={newOrg.entra_tenant_id}
                  onChange={(e) => setNewOrg({ ...newOrg, entra_tenant_id: e.target.value })}
                  placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 font-mono text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('organizations.domains') || 'Sallitut domainit'}
                </label>
                <input
                  type="text"
                  value={domainsInput}
                  onChange={(e) => setDomainsInput(e.target.value)}
                  placeholder="esim. yritys.fi, yritys.com (pilkulla erotettuna)"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Käyttäjät tunnistetaan organisaatioon sähköpostin domainin perusteella
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('organizations.allowedTenants') || 'Sallitut Entra Tenant ID:t'}
                </label>
                <input
                  type="text"
                  value={tenantsInput}
                  onChange={(e) => setTenantsInput(e.target.value)}
                  placeholder="Pilkulla erotettuna (valinnainen)"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 font-mono text-sm"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Jos käyttäjiä tulee useammasta Entra ID tenantista
                </p>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => { setIsModalOpen(false); resetForm(); }}
                  className="px-4 py-2 text-gray-700 border rounded-lg hover:bg-gray-50"
                >
                  {tc('common.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={createOrgMutation.isPending}
                  className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 disabled:opacity-50"
                >
                  {createOrgMutation.isPending ? tc('common.saving') : tc('common.save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
